import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { MapPinIcon, SearchIcon } from "@/components/icons/HomepageIcons";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Sparkles } from "lucide-react";
import biletimeLogo from "@/assets/biletime-logo.png";

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const settings = useSiteSettings();

  if (!settings.hero_enabled) return null;

  // Site-settings overrides are stored in Turkish by the admin.
  // Only honor them when the current language is Turkish; otherwise use i18n translations.
  const useOverride = lang === "tr";
  const title1 = (useOverride && settings.hero_title_line1?.trim()) || t("hero.title1");
  const title2 = (useOverride && settings.hero_title_line2?.trim()) || t("hero.title2");
  const subtitle = (useOverride && settings.hero_subtitle?.trim()) || t("hero.subtitle");
  const ctaLabel = (useOverride && settings.hero_cta_label?.trim()) || t("hero.discover");
  const searchPlaceholder = (useOverride && settings.hero_search_placeholder?.trim()) || t("hero.searchPlaceholder");

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/explore?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate("/explore");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <section
      className="relative h-[48vh] sm:h-[55vh] lg:h-[62vh] flex items-center justify-center overflow-hidden md:pt-12 pb-2 sm:pb-14"
      style={{ paddingBottom: "calc(var(--hero-pb) + 0.25rem)", touchAction: "pan-y", WebkitOverflowScrolling: "touch" }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <picture className="block w-full h-full">
          <source srcSet="/hero-concert-mobile.webp" type="image/webp" media="(max-width: 640px)" />
          <source srcSet="/hero-concert.webp" type="image/webp" />
          <img
            src="/hero-concert.jpg"
            alt="Concert atmosphere"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={1080}
            decoding="async"
          />
        </picture>
        {/* Layered overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/75 via-foreground/45 to-transparent" />
        {/* Smooth blend into background — uzun yumuşak geçiş */}
        <div className="absolute inset-x-0 bottom-0 h-48 sm:h-56 bg-gradient-to-b from-transparent via-background/70 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(var(--background)/0.35)_100%)]" />
        {/* Animated glow blobs */}
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-primary/30 blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-0 w-80 h-80 rounded-full bg-accent/20 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      <div className="relative z-10 container mx-auto px-4 text-center py-4 pt-[calc(env(safe-area-inset-top)+1rem)]" style={{ touchAction: "pan-y" }}>
        <div className="max-w-3xl mx-auto">


          <img
            src={biletimeLogo}
            alt="Biletime"
            width={440}
            height={174}
            className="mx-auto mb-4 sm:mb-5 h-28 sm:h-32 w-auto object-contain drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)] md:hidden"
            loading="eager"
            decoding="async"
          />
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary-foreground mb-3 sm:mb-6 leading-[1.05] tracking-tight drop-shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
            <span className="block">{title1}</span>
            <span className="block bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_4s_ease-in-out_infinite]">
              {title2}
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-primary-foreground/80 mb-5 sm:mb-7 max-w-xl mx-auto px-2 leading-relaxed">
            {subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto px-2">
            <div className="relative flex-1 group">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/40 to-accent/40 blur-md opacity-0 group-focus-within:opacity-100 transition-opacity" />
              <div className="relative">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className="w-full pl-12 pr-4 py-3.5 sm:py-4 rounded-2xl bg-card/90 backdrop-blur-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-base shadow-lg border border-border/50"
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              className="relative gradient-hero text-primary-foreground px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl font-semibold text-sm sm:text-base hover:scale-[1.02] active:scale-[0.98] transition-transform flex items-center justify-center gap-2 shadow-xl shadow-primary/30"
            >
              <MapPinIcon className="w-5 h-5" />
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
